import React, { useState, useEffect, useCallback } from 'react';
import {
  FaPlus, FaEdit, FaTrash, FaSpinner, FaExclamationCircle,
  FaChevronLeft, FaChevronRight, FaPercent,
} from 'react-icons/fa';
import { remiseService } from '../../../services/remiseService';

const PRESET_COLORS = [
  { color: '#FF6B6B', textColor: '#fff', label: 'Rouge' },
  { color: '#FFD93D', textColor: '#333', label: 'Jaune' },
  { color: '#6BCB77', textColor: '#fff', label: 'Vert' },
  { color: '#4D96FF', textColor: '#fff', label: 'Bleu' },
  { color: '#845EC2', textColor: '#fff', label: 'Violet' },
  { color: '#F9A828', textColor: '#fff', label: 'Orange' },
];

const DEFAULT_FORM = {
  pourcentage: '',
  probabilite: '',
  label: '',
  color: '#6BCB77',
  textColor: '#fff',
  isActive: true,
};

const RemisePage = () => {
  const [remises, setRemises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingRemise, setEditingRemise] = useState(null);
  const [deletingRemise, setDeletingRemise] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Remises Roulette | Admin';
  }, []);

  const fetchRemises = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await remiseService.getRemises(currentPage, pageSize, searchTerm);
      setRemises(response.data || []);
      setTotalPages(response.totalPages || 0);
    } catch {
      setError('Erreur lors du chargement des remises');
      setRemises([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => fetchRemises(), 400);
    return () => clearTimeout(t);
  }, [fetchRemises]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const openCreateModal = () => {
    setEditingRemise(null);
    setFormData(DEFAULT_FORM);
    setIsFormModalOpen(true);
  };

  const openEditModal = (remise) => {
    setEditingRemise(remise);
    setFormData({
      pourcentage: remise.pourcentage ?? '',
      probabilite: remise.probabilite != null ? (remise.probabilite * 100) : '',
      label: remise.label || '',
      color: remise.color || '#6BCB77',
      textColor: remise.textColor || '#fff',
      isActive: remise.isActive !== undefined ? remise.isActive : true,
    });
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (formSubmitting) return;
    setFormSubmitting(true);
    try {
      const payload = { ...formData, probabilite: parseFloat(formData.probabilite) / 100 };
      if (editingRemise) {
        await remiseService.updateRemise(editingRemise.id, payload);
        showSuccess('Remise mise à jour avec succès');
      } else {
        await remiseService.createRemise(payload);
        showSuccess('Remise créée avec succès');
      }
      setIsFormModalOpen(false);
      setCurrentPage(1);
      fetchRemises();
    } catch {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setFormSubmitting(false);
    }
  };

  const openDeleteModal = (remise) => {
    setDeletingRemise(remise);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (formSubmitting) return;
    setFormSubmitting(true);
    try {
      await remiseService.deleteRemise(deletingRemise.id);
      showSuccess('Remise supprimée avec succès');
      setIsDeleteModalOpen(false);
      fetchRemises();
    } catch {
      setError('Erreur lors de la suppression');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Remises Roulette</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gérez les niveaux de remise affichés sur la roue de la fortune
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="mt-4 sm:mt-0 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          <FaPlus className="h-4 w-4" />
          Ajouter une remise
        </button>
      </div>

      {/* Alerts */}
      {successMessage && (
        <div className="mb-4 rounded-md bg-green-50 border border-green-200 p-4 flex items-center gap-2">
          <span className="text-green-700 text-sm">{successMessage}</span>
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-4 flex items-center gap-2">
          <FaExclamationCircle className="text-red-500 h-4 w-4 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700 text-sm">✕</button>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Rechercher par label..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          className="w-full sm:w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      {/* List */}
      <div className="bg-white shadow sm:rounded-md">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <FaSpinner className="animate-spin text-emerald-600 h-8 w-8" />
          </div>
        ) : remises.length === 0 ? (
          <div className="text-center py-16 text-gray-500 text-sm">Aucune remise trouvée</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {remises.map((remise) => (
              <li key={remise.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  {/* Color preview circle */}
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: remise.color || '#6BCB77' }}
                  >
                    <FaPercent style={{ color: remise.textColor || '#fff' }} className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{remise.label || `${remise.pourcentage}%`}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        remise.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {remise.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div className="flex gap-4 mt-0.5 text-xs text-gray-500">
                      <span>Pourcentage : <strong className="text-gray-700">{remise.pourcentage}%</strong></span>
                      <span>Probabilité : <strong className="text-gray-700">{(remise.probabilite * 100).toFixed(4)}%</strong></span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(remise)}
                    className="p-2 text-gray-400 hover:text-emerald-600 rounded-md hover:bg-emerald-50 transition-colors"
                    title="Modifier"
                  >
                    <FaEdit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(remise)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                    title="Supprimer"
                  >
                    <FaTrash className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
          <div className="flex items-center gap-3">
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} className="border border-gray-300 rounded-md text-sm py-1.5 pl-2 pr-7 focus:ring-emerald-500 focus:border-emerald-500">
              {[5, 10, 25, 50].map(s => <option key={s} value={s}>{s} / page</option>)}
            </select>
            <span className="text-sm text-gray-600">Page {currentPage} / {totalPages}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-md disabled:opacity-40 hover:bg-gray-50"
            >
              <FaChevronLeft className="h-3 w-3" /> Précédent
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-md disabled:opacity-40 hover:bg-gray-50"
            >
              Suivant <FaChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingRemise ? 'Modifier la remise' : 'Nouvelle remise'}
              </h3>
            </div>
            <form onSubmit={handleFormSubmit} className="px-6 py-4 space-y-4">
              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label affiché</label>
                <input
                  name="label"
                  value={formData.label}
                  onChange={handleChange}
                  required
                  placeholder="ex: 50%"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Pourcentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pourcentage de remise (%)
                </label>
                <input
                  name="pourcentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.pourcentage}
                  onChange={handleChange}
                  required
                  placeholder="ex: 50"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Probabilite */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Probabilité de gain (%)
                </label>
                <input
                  name="probabilite"
                  type="number"
                  min="0"
                  max="100"
                  step="0.0001"
                  value={formData.probabilite}
                  onChange={handleChange}
                  required
                  placeholder="ex: 1 = 1% de chance"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                {formData.probabilite && (
                  <p className="mt-1 text-xs text-gray-500">
                    = {parseFloat(formData.probabilite || 0).toFixed(4)}% de chance de gagner
                  </p>
                )}
              </div>

              {/* Color presets */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Couleur du segment</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {PRESET_COLORS.map((p) => (
                    <button
                      key={p.color}
                      type="button"
                      title={p.label}
                      onClick={() => setFormData((prev) => ({ ...prev, color: p.color, textColor: p.textColor }))}
                      className={`h-8 w-8 rounded-full border-2 transition-all ${
                        formData.color === p.color ? 'border-gray-800 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: p.color }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Couleur fond</label>
                    <input
                      type="color"
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      className="h-8 w-16 rounded cursor-pointer border border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Couleur texte</label>
                    <input
                      type="color"
                      name="textColor"
                      value={formData.textColor}
                      onChange={handleChange}
                      className="h-8 w-16 rounded cursor-pointer border border-gray-300"
                    />
                  </div>
                  {/* Preview */}
                  <div className="flex-1 flex justify-end">
                    <div
                      className="h-12 w-20 rounded-lg flex items-center justify-center text-sm font-bold shadow"
                      style={{ backgroundColor: formData.color, color: formData.textColor }}
                    >
                      {formData.label || `${formData.pourcentage}%`}
                    </div>
                  </div>
                </div>
              </div>

              {/* IsActive */}
              <div className="flex items-center gap-2">
                <input
                  id="isActive"
                  name="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Remise active</label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-md flex items-center gap-2"
                >
                  {formSubmitting && <FaSpinner className="animate-spin h-3 w-3" />}
                  {editingRemise ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {isDeleteModalOpen && deletingRemise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <FaExclamationCircle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Confirmer la suppression</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Supprimer la remise <strong>{deletingRemise.label || `${deletingRemise.pourcentage}%`}</strong> ?
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={formSubmitting}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-md flex items-center gap-2"
              >
                {formSubmitting && <FaSpinner className="animate-spin h-3 w-3" />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemisePage;
